/**
 * Breakpoint Service - A365 Core
 * 
 * Servicio unificado para la detección de breakpoints de Tailwind CSS.
 * Elimina la dependencia de Material-UI useTheme/useMediaQuery y react-responsive.
 * 
 * Breakpoints de Tailwind CSS:
 * - xs: 0px - 639px
 * - sm: 640px - 767px  
 * - md: 768px - 1023px
 * - lg: 1024px - 1279px
 * - xl: 1280px - 1535px
 * - 2xl: 1536px+
 * 
 * @author A365 Core Team
 * @version 1.0.0
 */

// Definición de breakpoints alineados con Tailwind CSS
export const TAILWIND_BREAKPOINTS = {
  xs: { min: 0, max: 639 },
  sm: { min: 640, max: 767 },
  md: { min: 768, max: 1023 },
  lg: { min: 1024, max: 1279 },
  xl: { min: 1280, max: 1535 },
  '2xl': { min: 1536, max: Infinity }
};

// Nombres ordenados de breakpoints para facilitar comparaciones
export const BREAKPOINT_ORDER = ['xs', 'sm', 'md', 'lg', 'xl', '2xl'];

/**
 * Obtiene el breakpoint actual basado en el ancho de la ventana
 * @param {number} width - Ancho de la ventana (opcional, usa window.innerWidth por defecto)
 * @returns {string} Nombre del breakpoint actual ('xs', 'sm', 'md', 'lg', 'xl', '2xl')
 */
export const getCurrentBreakpoint = (width = null) => {
  const windowWidth = width !== null ? width : 
    (typeof window !== 'undefined' ? window.innerWidth : 1024);
  
  for (const [breakpoint, { min, max }] of Object.entries(TAILWIND_BREAKPOINTS)) {
    if (windowWidth >= min && windowWidth <= max) {
      return breakpoint;
    }
  }
  
  return 'lg'; // fallback
};

/**
 * Verifica si el breakpoint actual es menor o igual al especificado
 * @param {string} targetBreakpoint - Breakpoint objetivo
 * @param {string} currentBreakpoint - Breakpoint actual (opcional)
 * @returns {boolean}
 */
export const isBreakpointDown = (targetBreakpoint, currentBreakpoint = null) => {
  const current = currentBreakpoint || getCurrentBreakpoint();
  const currentIndex = BREAKPOINT_ORDER.indexOf(current);
  const targetIndex = BREAKPOINT_ORDER.indexOf(targetBreakpoint);
   
  return currentIndex <= targetIndex;
};

/**
 * Verifica si el breakpoint actual es mayor o igual al especificado
 * @param {string} targetBreakpoint - Breakpoint objetivo
 * @param {string} currentBreakpoint - Breakpoint actual (opcional)
 * @returns {boolean}
 */
export const isBreakpointUp = (targetBreakpoint, currentBreakpoint = null) => {
  const current = currentBreakpoint || getCurrentBreakpoint();
  const currentIndex = BREAKPOINT_ORDER.indexOf(current);
  const targetIndex = BREAKPOINT_ORDER.indexOf(targetBreakpoint);
  
  return currentIndex >= targetIndex;
};

/**
 * Verifica si el dispositivo es móvil (breakpoint xl o menor)
 * Equivalente a useMediaQuery(theme.breakpoints.down('xl'))
 * @param {string} currentBreakpoint - Breakpoint actual (opcional)
 * @returns {boolean}
 */
export const isMobile = (currentBreakpoint = null) => {
  return isBreakpointDown('xl', currentBreakpoint);
};

/**
 * Verifica si el dispositivo es tablet (md o lg)
 * @param {string} currentBreakpoint - Breakpoint actual (opcional)
 * @returns {boolean}
 */
export const isTablet = (currentBreakpoint = null) => {
  const current = currentBreakpoint || getCurrentBreakpoint();
  return current === 'md' || current === 'lg';
};

/**
 * Verifica si el dispositivo es desktop (xl o mayor)
 * @param {string} currentBreakpoint - Breakpoint actual (opcional)
 * @returns {boolean}
 */
export const isDesktop = (currentBreakpoint = null) => {
  return isBreakpointUp('xl', currentBreakpoint);
};

/**
 * Obtiene el valor numérico mínimo de un breakpoint
 * @param {string} breakpoint - Nombre del breakpoint
 * @returns {number} Valor mínimo en píxeles
 */
export const getBreakpointValue = (breakpoint) => {
  return TAILWIND_BREAKPOINTS[breakpoint]?.min || 0;
};
